import { Section, useConfig } from '@seek/libs-shared';
import { useMelwaysLink } from '@seek/melways-react';
import type { Country } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Autosuggest } from 'braid-design-system';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CompanyProfilesPage, ROOT_PATH } from '../../../../paths';
import type { SalariesTabModel } from '../../../pages/salaries/salariesTabModel';
import translations from './.vocab/index';

interface SalariesSearchSectionProps {
  model: Pick<SalariesTabModel, 'companyName' | 'salaryRanges'>;
}

interface AutosuggestValue {
  text: string;
  description?: string;
  value?: string;
}

const MINIMUM_ROLES_TO_SHOW = 6;

export const SalariesSearchSection = ({
  model,
}: SalariesSearchSectionProps) => {
  const { companyName, salaryRanges } = model;
  const salaryRange = salaryRanges?.salaryRange;

  const roleSuggestions: AutosuggestValue[] =
    salaryRange?.map((range) => ({
      text: range.roleTitle,
      value: range.roleId,
    })) || [];
  const { t } = useTranslations(translations);
  const { country, language } = useConfig();
  const navigate = useNavigate();
  const params = useParams();
  const localize = useMelwaysLink();

  const [role, setRole] = useState<AutosuggestValue>({
    text: '',
  });

  const countryNameMapper: Record<Country, string> = {
    au: t('Australia'),
    hk: t('Hong Kong'),
    id: t('Indonesia'),
    my: t('Malaysia'),
    nz: t('New Zealand'),
    ph: t('Philippines'),
    sg: t('Singapore'),
    th: t('Thailand'),
  };

  const isEnoughRoles = roleSuggestions.length >= MINIMUM_ROLES_TO_SHOW;

  if (isEnoughRoles) {
    return (
      <Section
        mode={'view'}
        heading={t('Company salaries', { companyName })}
        headingDescription={t('Explore how much...', {
          companyName,
          country: countryNameMapper[country],
        })}
        padding={'none'}
      >
        <Autosuggest
          id="roleFilter"
          placeholder={`e.g. ${roleSuggestions[0].text}`}
          aria-label={'Search for job roles'}
          value={role}
          suggestions={roleSuggestions}
          onChange={(e) => {
            setRole({ text: e.text });
            if (e.value && params.companySlug) {
              const viewSalaryByRoleLink = localize({
                path: `/${ROOT_PATH}/${params.companySlug}/${CompanyProfilesPage.Salaries}/${e.value}`,
                language,
              });
              void navigate(viewSalaryByRoleLink);
            }
          }}
          onClear={() => {
            setRole({ text: '' });
          }}
        />
      </Section>
    );
  } else {
    return null;
  }
};
